import React, { useState } from 'react'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemText from '@mui/material/ListItemText'
import ListItemSecondaryAction from '@mui/material/ListItemSecondaryAction'
import ListItemIcon from '@mui/material/ListItemIcon'
import Typography from '@mui/material/Typography'
import Skeleton from '@mui/material/Skeleton'
import { Badge, Chip } from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faKey } from '@fortawesome/pro-solid-svg-icons'
import IconTooltipButton from '../../../../../button/IconTooltipButton'

const PROVIDER_LABEL = {
  'acer-oars': 'ACER OARS (PAT)'
}

const STATUS_CHIP = {
  running: { label: 'Syncing…', color: 'warning' },
  'awaiting-code': { label: 'Code needed', color: 'warning' }, // MF-594: ACER sent a code by email
  ok: { label: 'Last sync OK', color: 'success' },
  error: { label: 'Last sync failed', color: 'error' }
}

const formatDate = (iso) => {
  if (!iso) return null
  return new Date(iso).toLocaleString('en-AU', {
    dateStyle: 'medium',
    timeStyle: 'short'
  })
}

const DAY_MS = 24 * 60 * 60 * 1000

// MF-594 — Does ACER need a code on the next sync? It remembers the connection for 30 days after a code.
const rememberedNote = (item) => {
  if (item.provider !== 'acer-oars' || item.lastSyncStatus === 'awaiting-code') return null
  if (item.hasDeviceTrust && item.deviceTrustExpiresAt) {
    const until = new Date(item.deviceTrustExpiresAt)
    const soon = until.getTime() - Date.now() < 5 * DAY_MS
    const date = until.toLocaleDateString('en-AU', { dateStyle: 'medium' })
    return soon
      ? {
          warn: true,
          text: `ACER stops remembering this connection on ${date}: it will ask for a new code then.`
        }
      : { warn: false, text: `No code needed until ${date}.` }
  }
  return { warn: false, text: 'ACER will ask for a verification code on the next sync.' }
}

const ThirdPartyAccessList = ({
  list = [],
  isLoading,
  permissions = {},
  onModal,
  onSync,
  onHistory,
  onEnterCode
}) => {
  const [syncingId, setSyncingId] = useState(null)

  const handleSync = async (item) => {
    setSyncingId(item._id)
    try {
      await onSync(item._id)
    } finally {
      setSyncingId(null)
    }
  }

  if (isLoading) {
    return (
      <List>
        {Array(3)
          .fill()
          .map((_, i) => (
            <ListItem key={i}>
              <ListItemIcon>
                <Skeleton variant='circular' width='2rem' height='2rem' />
              </ListItemIcon>
              <ListItemText
                primary={<Skeleton variant='text' width={300} />}
                secondary={<Skeleton variant='text' width={200} />}
              />
            </ListItem>
          ))}
      </List>
    )
  }

  if (list.length === 0) {
    return (
      <Typography variant='inherit' component='p' className='text-muted-foreground opacity-50 py-2 px-2'>
        You don&apos;t have any external credential yet
      </Typography>
    )
  }

  return (
    <List>
      {list.map((item) => {
        const status = STATUS_CHIP[item.lastSyncStatus]
        const lastSync = formatDate(item.lastSyncAt)
        return (
          // pr reserves the room of the action buttons (they are absolutely positioned): a long message must never run
          // underneath them
          <ListItem key={item._id} sx={{ pr: 22 }}>
            <ListItemIcon>
              <Badge
                color={item.enabled ? 'primary' : 'inherit'}
                badgeContent={item.enabled ? 'On' : 'Off'}
                variant='dot'
              >
                <FontAwesomeIcon className='text-2xl' icon={['fas', 'plug']} />
              </Badge>
            </ListItemIcon>
            <ListItemText
              primary={
                <span className='flex items-center gap-2'>
                  {item.label || PROVIDER_LABEL[item.provider] || item.provider}
                  {status && <Chip label={status.label} color={status.color} size='small' />}
                </span>
              }
              secondary={
                <>
                  {PROVIDER_LABEL[item.provider] || item.provider} · {item.schoolSlug} · {item.username}
                  {lastSync && ` · last sync: ${lastSync}`}
                  {(() => {
                    const note = rememberedNote(item)
                    return note ? (
                      <Typography
                        component='span'
                        variant='caption'
                        color={note.warn ? 'warning.main' : 'text.secondary'}
                        sx={{ display: 'block', mt: 0.25, lineHeight: 1.35 }}
                      >
                        {note.text}
                      </Typography>
                    ) : null
                  })()}
                  {['error', 'awaiting-code'].includes(item.lastSyncStatus) && item.lastSyncError && (
                    <Typography
                      component='span'
                      variant='caption'
                      color={item.lastSyncStatus === 'error' ? 'error' : 'warning.main'}
                      sx={{ display: 'block', mt: 0.5, lineHeight: 1.35, overflowWrap: 'anywhere' }}
                    >
                      {item.lastSyncError}
                    </Typography>
                  )}
                </>
              }
            />
            <ListItemSecondaryAction>
              {permissions['sync'] && item.lastSyncStatus === 'awaiting-code' && (
                <IconTooltipButton
                  title='Enter the verification code'
                  icon={faKey}
                  onClick={() => onEnterCode(item)}
                />
              )}
              {permissions['sync'] && (
                <IconTooltipButton
                  title='Sync now'
                  icon={syncingId === item._id ? 'circle-notch' : ['fas', 'sync']}
                  spin={syncingId === item._id}
                  disabled={!item.enabled || syncingId === item._id}
                  onClick={() => handleSync(item)}
                />
              )}
              {permissions['history'] && (
                <IconTooltipButton
                  title='Sync history'
                  icon={['fas', 'history']}
                  onClick={() => onHistory(item)}
                />
              )}
              {permissions['updated'] && (
                <IconTooltipButton
                  title='Edit'
                  icon={['fas', 'edit']}
                  onClick={() => onModal('edit', item)}
                />
              )}
              {permissions['deleted'] && (
                <IconTooltipButton
                  title='Delete'
                  icon={['fas', 'trash-alt']}
                  onClick={() => onModal('delete', item)}
                />
              )}
            </ListItemSecondaryAction>
          </ListItem>
        )
      })}
    </List>
  )
}

export default ThirdPartyAccessList
